import { useEffect, useState, useMemo } from 'react';
import { ShieldAlert, Loader2, CreditCard, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const parseDate = (s) => {
  if (!s) return null;
  if (s.includes('/')) {
    const [d, m, y] = s.split('/').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const daysRemaining = (visa) => {
  const start = parseDate(visa.entry_date || visa.issue_date);
  if (start && visa.duration_days) {
    const exp = new Date(start);
    exp.setDate(exp.getDate() + parseInt(visa.duration_days));
    return Math.ceil((exp - new Date().setHours(0, 0, 0, 0)) / 86400000);
  }
  const exp = parseDate(visa.expiry_date);
  if (!exp) return null;
  return Math.ceil((exp - new Date().setHours(0, 0, 0, 0)) / 86400000);
};

const badge = (days) => {
  if (days === null) return { text: '—', cls: 'bg-slate-600/30 text-slate-300 border-slate-500/40' };
  if (days <= 0) return { text: 'منتهية', cls: 'bg-red-500/20 text-red-400 border-red-500/50' };
  if (days <= 5) return { text: `${days} أيام`, cls: 'bg-amber-500/20 text-amber-400 border-amber-500/50' };
  return { text: `${days} يوم`, cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' };
};

// Read-only visa board built for embedding (iframe) inside the contracts system.
export default function VisaEmbedPage() {
  const [visas, setVisas] = useState([]);
  const [state, setState] = useState('loading'); // loading | ok | denied | error
  const pin = useMemo(() => new URLSearchParams(window.location.search).get('pin') || '', []);

  useEffect(() => {
    const load = async () => {
      if (!pin) { setState('denied'); return; }
      try {
        const res = await fetch(`${API}/api/visa-tracker/visas?pin=${encodeURIComponent(pin)}&light=true`);
        if (res.status === 401) { setState('denied'); return; }
        if (!res.ok) { setState('error'); return; }
        const data = await res.json();
        setVisas(data.visas || []);
        setState('ok');
      } catch (e) {
        setState('error');
      }
    };
    load();
  }, [pin]);

  const sorted = useMemo(() => {
    return [...visas]
      .map((v) => ({ ...v, _days: daysRemaining(v) }))
      .sort((a, b) => (a._days ?? 9999) - (b._days ?? 9999));
  }, [visas]);

  const stats = useMemo(() => ({
    expired: sorted.filter((v) => v._days !== null && v._days <= 0).length,
    soon: sorted.filter((v) => v._days !== null && v._days > 0 && v._days <= 5).length,
    active: sorted.filter((v) => v._days !== null && v._days > 5).length,
  }), [sorted]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-[#0B1B2B] flex items-center justify-center text-white gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-orange-400" />
        <span>جارٍ تحميل التأشيرات...</span>
      </div>
    );
  }

  if (state !== 'ok') {
    return (
      <div className="min-h-screen bg-[#0B1B2B] flex flex-col items-center justify-center text-white gap-3" data-testid="visa-embed-denied">
        <ShieldAlert className="h-10 w-10 text-red-400" />
        <p className="text-lg font-bold">{state === 'denied' ? 'رمز الدخول غير صحيح' : 'تعذّر تحميل البيانات'}</p>
        <p className="text-sm text-white/50">تأكد من صحة الرابط المستخدم في نظام العقود</p>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-[#0B1B2B] text-white p-4 sm:p-6" data-testid="visa-embed-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black">تذكير التأشيرات — TADABER</h1>
            <p className="text-white/50 text-xs flex items-center gap-1">
              <Clock className="h-3 w-3" /> عرض للقراءة فقط · {new Date().toLocaleDateString('ar-AE')}
            </p>
          </div>
        </div>
        <div className="flex gap-2 text-xs font-bold">
          <span className="px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-400" data-testid="embed-stat-expired">منتهية {stats.expired}</span>
          <span className="px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-400" data-testid="embed-stat-soon">تنتهي قريباً {stats.soon}</span>
          <span className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-400" data-testid="embed-stat-active">نشطة {stats.active}</span>
        </div>
      </div>

      {/* Visa cards */}
      {sorted.length === 0 ? (
        <p className="text-white/50 text-center py-10">لا توجد تأشيرات</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sorted.map((v) => {
            const b = badge(v._days);
            return (
              <div
                key={v.id}
                className="bg-[#0F2540] border border-[#004B93]/30 rounded-xl p-4 flex flex-col gap-2"
                data-testid={`embed-visa-${v.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold truncate">{v.passenger_name || '—'}</p>
                    <p className="text-white/50 text-xs font-mono">{v.passport_number || ''}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg border text-xs font-bold whitespace-nowrap ${b.cls}`}>
                    {b.text}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-black/20 rounded-lg p-2">
                    <p className="text-white/40">الدخول</p>
                    <p className="font-medium">{v.entry_date || '—'}</p>
                  </div>
                  <div className="bg-black/20 rounded-lg p-2">
                    <p className="text-white/40">الانتهاء</p>
                    <p className="font-medium">{v.expiry_date || '—'}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
